<template>
  <v-container
    id="view-credit-billing-bills"
    fluid
    v-if="loaded"
  >
    <v-row>
      <v-col cols="12">
        <Breadcrumb :items="items" ></Breadcrumb>
        <h1>
          All {{ billData.development }} Bills
        </h1>

      </v-col>

      <v-row>
        <v-col cols="12">
          <v-alert
            v-if="billData.external_bills > 0"
            type="warning"
            class="warn"
            prominent
            border="left"
          >
            <strong>One or more bills require your approval</strong>
            <p>One or more bills below requires your approval (marked 'Agent Checks'). <span v-if="billData.report_notification_emails">An approval e-mail was sent to {{ billData.report_notification_emails }}</span></p>
          </v-alert>
        </v-col>
      </v-row>

      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-spacer></v-spacer>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>

          </v-card-title>

          <v-card-text style="text-align: right">
            <strong>Multiple PDF Exports</strong>
            <p>Export multiple bill/credit PDFs as a zip file. Check each below and select download</p>
            <p>{{ selectedDownload.bills.length }} Bill(s) Selected, {{ selectedDownload.credits.length }} Credit(s) Selected</p>
            <v-btn
              :disabled="totalBillsSelected === 0"
              @click="downloadBills"
              class="ma-2"
              color="green"  role="link">
              Export
            </v-btn>
            <v-btn
              :disabled="totalBillsSelected === 0"
              @click="resetDownloads"
              class="ma-2"
              color="blue"  role="link">
              Clear All
            </v-btn>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="billData.bills"
            :search="search"
            item-key="id"
            :items-per-page=100
            :footer-props="listSize"
            class="elevation-1"
          >
            <template #item.bill_total="{ value }">
              £{{ value }}
            </template>
            <template #item.type="{ value }">
              <v-chip
                v-if="value === 'apartment'"
                color="grey"
                dark
                small
              >
                Apartment
              </v-chip>
              <v-chip
                v-else
                color="#EF9273"
                dark
                small
              >
                Meter
              </v-chip>

            </template>
            <template #item.state="{ value, item }">
              <v-chip
                v-if="item.is_credit"
                color="grey"
                dark
                small
              >
                Credited
              </v-chip>
              <v-chip
                v-else
                :color="getBillState(value, 'color')"
                dark
                small
              >
                {{ getBillState(value, 'name') }}
              </v-chip>


            </template>

            <template #item.occupant_type="{ value }">
              <v-chip
                v-if="value === 'lease'"
                color="#1867c0"
                dark
                small
              >
                Lease
              </v-chip>
              <v-chip
                v-else-if="value === 'sub'"
                color="#5cbbf6"
                dark
                small
              >
                Sub Lease
              </v-chip>
              <v-chip
                v-else
                color="grey"
                dark
                small
              >
                Agent
              </v-chip>


            </template>
            <template #item.id="{ value, item }">
              <div v-if="item.is_bill">
                <v-checkbox
                  style="display: inline-block;"
                  v-model="selectedDownload.bills"
                  title="Add to download group"
                  :value="value"
                ></v-checkbox>
                <v-btn
                  x-small
                  :loading="downloadLoading"
                  :disabled="downloadLoading"
                  color="blue-grey"
                  class="ma-2 white--text"
                  style="margin-top: -2px !important;"
                  fab
                  @click="downloadBill(value, item.bill_file_name)"
                >
                  <v-icon dark>
                    mdi-cloud-download
                  </v-icon>
                </v-btn>
              </div>
            </template>

            <template #item.approval="{ value, item }">
              <v-btn
                v-if="item.is_approval_file"
                x-small
                :loading="downloadLoading"
                :disabled="downloadLoading"
                color="blue-grey"
                class="ma-2 white--text"
                fab
                @click="downloadBillApproval(value, item.bill_file_name)"
              >
                <v-icon dark>
                  mdi-cloud-download
                </v-icon>
              </v-btn>
            </template>

            <template #item.is_credit="{ value, item }">
              <div v-if="value">
                <v-checkbox
                  style="display: inline-block;"
                  v-model="selectedDownload.credits"
                  title="Add to download group"
                  :value="item.id"
                ></v-checkbox>
                <v-btn
                  x-small
                  :loading="downloadLoading"
                  :disabled="downloadLoading"
                  color="blue-grey"
                  class="ma-2 white--text"
                  style="margin-top: -2px !important;"
                  fab
                  @click="downloadBillCredit(item.id, item.bill_file_name)"
                >
                  <v-icon dark>
                    mdi-cloud-download
                  </v-icon>
                </v-btn>
              </div>
            </template>

          </v-data-table>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script>
import {get} from "vuex-pathify";
import Breadcrumb from "@/components/Breadcrumb";
import {date} from "@/mixins/date";
import {creditBilling} from "@/mixins/credit-billing";
import {dataTables} from "@/mixins/data-tables";

export default {
  name: "CreditBillingBills",
  props: {
    id: String
  },
  components: {
    Breadcrumb
  },
  mixins: [date, creditBilling, dataTables],
  computed: {
    billData: get('creditBilling/developmentBills'),
    totalBillsSelected() {
      return this.selectedDownload.bills.length + this.selectedDownload.credits.length;
    },
    items() {
      return [
        {
          text: 'All Developments',
          disabled: false,
          exact: true,
          to: '/credit-billing',
        },
        {
          text: this.billData.development,
          disabled: false,
          exact: true,
          to: '/credit-billing/' + this.id,
        },
        {
          text: "All Bills",
          disabled: true,
        }
      ];
    }
  },
  created() {
    this.$store.dispatch("loader/loaderCancel");
    this.fetchData();
  },
  data: (vm) => ({
    loaded: true,
    downloadLoading: false,
    selectedDownload: {
      credits: [],
      bills: []
    },
    listSize: {
      "items-per-page-options": [10, 25, 50, 100]
    },
    search: '',
    headers: [
      {
        text: 'Name',
        align: 'start',
        value: 'name',
      },
      {text: "Building", value: 'building_name'},
      {text: "Status", value: 'state'},
      {text: 'Type', value: 'type'},
      {text: 'Occupant', value: 'occupant_type'},
      {text: 'From', value: 'starts', sort: (a, b) => {
          return vm.dateSort(a,b);
        }},
      {text: 'To', value: 'ends', sort: (a, b) => {
          return vm.dateSort(a,b);
        }},
      {text: 'Total', value: 'bill_total'},
      {text: 'Invoice', value: 'invoice_number'},
      {text: 'Invoice Date', value: 'invoice_date', sort: (a, b) => {
          return vm.dateSort(a,b);
        }},
      {text: 'Related Approval', value: 'approval'},
      {text: 'Bill PDF', value: 'id'},
      {text: 'Credit PDF', value: 'is_credit'},
    ],
  }),
  methods: {
    resetDownloads() {
      this.selectedDownload.bills = [];
      this.selectedDownload.credits = [];
    },
    async fetchData() {
      await this.$store.dispatch("loader/loaderInitiate", {title: "Loading Development Bills"});


      const success = await this.$store.dispatch("creditBilling/getDevelopmentBills", {
        developmentRef: this.id
      });

      if (!success) {
        await this.$router.push("/credit-billing/" + this.id);
        return;
      }

      this.loaded = true;

      await this.$store.dispatch("loader/loaderCancel");

    },
    formatterDate(value) {
      if (!value) {
        return "";
      }
      return this.formatDateTimezone(value);
    },
    downloadBill(fileRef, name) {
      const file = "bill_" + name + '.pdf';
      this.genericDownload('download-bill', fileRef, file);
    },
    downloadBillCredit(fileRef, name) {
      const file = "credit_" + name + '.pdf';
      this.genericDownload('download-credit', fileRef, file);
    },
    downloadBillApproval(fileRef, name) {
      const file = "approval_" + name + '.csv';
      this.genericDownload('download-approval', fileRef, file);
    },
    async genericDownload(routePath, fileRef, name) {
      this.downloadLoading = true;

      await this.triggerCreditFileDownload(routePath, fileRef, name);

      this.downloadLoading = false;
    },
    async downloadBills() {
      if (this.selectedDownload.bills.length === 0 && this.selectedDownload.credits.length === 0) {
        await this.$store.dispatch("notification/triggerError", { detail: "Please select at lease one bill or credit" });
        return;
      }

      await this.$store.dispatch("loader/loaderInitiate", { title: "Requesting Download" });

      const isSuccess = await this.$store.dispatch("downloads/requestBills", {developmentRef: this.id, billIds: this.selectedDownload.bills, creditIds: this.selectedDownload.credits});

      if (isSuccess) {
        this.resetDownloads();
        await this.$store.dispatch("notification/triggerSuccess", { detail: "Your file will be available from your downloads page shortly", title: "Download requested" });
      }

      await this.$store.dispatch("loader/loaderCancel");
    }
  }
}
</script>

<style lang="scss" scoped>
.warn p {
  margin-bottom: 0px;
  font-size: 0.9rem;
}
</style>
